import React, { useCallback, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Check, Copy, Eye, Link2, Loader2, Send, ShieldOff, Users } from "lucide-react";

const DAYS = [1, 7, 30];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/* 批准图纸「发给分包」：选有效期 → 一键生成免登录链接（复制 / 二维码 / 撤销）；旁边保留「加入 RFQ 资料包并通知」。 */
export const PlanShareDialog = ({ open, onClose, api, zh, projectId, file, onRfq }) => {
  const [days, setDays] = useState(7);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = (l) => `${window.location.origin}${l.path}`;
  const load = useCallback(() => {
    if (!file) return;
    api.get(`/projects/${projectId}/files/${file.id}/plan-shares`).then(({ data }) => setLinks(data || [])).catch(() => setLinks([]));
  }, [api, projectId, file]);
  useEffect(() => { if (open) { setFresh(null); setCopied(false); load(); } }, [open, load]);

  const copy = async (l, quiet = false) => {
    try { await navigator.clipboard.writeText(url(l)); setCopied(true); toast.success(zh ? "链接已复制" : "Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { if (!quiet) toast.error(zh ? "复制失败，请手动复制" : "Copy failed"); }
  };
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/projects/${projectId}/files/${file.id}/plan-shares`, { days }); setFresh(data); load(); copy(data, true); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setBusy(false);
  };
  const revoke = async (l) => {
    try { await api.post(`/plan-shares/${l.id}/revoke`); toast.success(zh ? "链接已撤销" : "Link revoked"); if (fresh?.id === l.id) setFresh(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "撤销失败" : "Failed")); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md z-[97]" overlayClassName="z-[96]" data-testid="plan-share-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Send className="h-4 w-4 text-brand-500" />{zh ? "发给分包 · 图纸链接" : "Send to subcontractor · plan link"}</DialogTitle>
          <DialogDescription className="sr-only">Create an expiring public link to the approved plan</DialogDescription>
        </DialogHeader>
        {file && (
          <div className="space-y-3 text-xs">
            <p className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 text-slate-700 truncate" data-testid="plan-share-file">{file.filename}{file.pageCount ? <span className="text-slate-400"> · {file.pageCount} {zh ? "页" : "p"}</span> : null}</p>
            <div>
              <p className="text-slate-500 mb-1.5">{zh ? "有效期（到期后链接自动失效，也可随时撤销）" : "Expires in (auto-expires; revoke anytime)"}</p>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" role="radiogroup">
                {DAYS.map((d) => <button key={d} type="button" role="radio" aria-checked={days === d} data-testid={`plan-share-days-${d}`} onClick={() => setDays(d)} className={`px-3 h-7 rounded-md font-medium transition-colors ${days === d ? "bg-brand-500 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}>{d} {zh ? "天" : d === 1 ? "day" : "days"}</button>)}
              </div>
            </div>
            <Button onClick={create} disabled={busy} className="w-full bg-brand-500 hover:bg-brand-600 h-9" data-testid="plan-share-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Link2 className="h-3.5 w-3.5 mr-1.5" />}{zh ? "生成链接并复制" : "Create link & copy"}</Button>
            {fresh && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 flex gap-3 items-center" data-testid="plan-share-fresh">
                <QRCodeCanvas value={url(fresh)} size={72} data-testid="plan-share-qr" />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-mono text-[10px] text-slate-700 break-all" data-testid="plan-share-url">{url(fresh)}</p>
                  <p className="text-[10px] text-emerald-700">{zh ? "有效至" : "Valid until"} {fmt(fresh.expiresAt)}</p>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => copy(fresh)} data-testid="plan-share-copy">{copied ? <Check className="h-3 w-3 mr-1 text-emerald-600" /> : <Copy className="h-3 w-3 mr-1" />}{zh ? "复制" : "Copy"}</Button>
                </div>
              </div>
            )}
            {links.length > 0 && (
              <div className="space-y-1" data-testid="plan-share-list">
                <p className="text-slate-500 font-medium">{zh ? "有效链接" : "Active links"} · {links.length}</p>
                {links.map((l) => (
                  <div key={l.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5" data-testid={`plan-share-row-${l.id}`}>
                    <span className="font-mono text-[10px] text-slate-500 truncate flex-1">…{l.token.slice(-8)}</span>
                    <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5" title={zh ? "浏览次数" : "Views"}><Eye className="h-3 w-3" />{l.views}</span>
                    <span className="text-[10px] text-slate-400">{fmt(l.expiresAt)}</span>
                    <button type="button" onClick={() => copy(l)} className="text-slate-400 hover:text-brand-500" title={zh ? "复制" : "Copy"} data-testid={`plan-share-copy-${l.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => revoke(l)} className="text-slate-400 hover:text-red-600" title={zh ? "撤销" : "Revoke"} data-testid={`plan-share-revoke-${l.id}`}><ShieldOff className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
              <button type="button" onClick={() => { onClose(); setTimeout(() => onRfq?.(file), 260); }} className="inline-flex items-center gap-1 text-slate-500 hover:text-brand-600 hover:underline" data-testid="plan-share-rfq"><Users className="h-3.5 w-3.5" />{zh ? "加入 RFQ 资料包并通知分包" : "Add to RFQ pack & notify subs"}</button>
              <Button variant="outline" size="sm" onClick={onClose} data-testid="plan-share-close">{zh ? "关闭" : "Close"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
